import React, { createContext, useReducer, useEffect, useCallback } from 'react';
import db from '../services/db';

const GridContext = createContext(null);

const DEFAULT_GRID_CONFIG = { id: 'default', rows: 4, cols: 4 };

const initialState = {
  gridConfig: null,
  loading: true,
  error: null,
};

function gridReducer(state, action) {
  switch (action.type) {
    case 'LOAD_CONFIG_SUCCESS':
      return { ...state, gridConfig: action.payload, loading: false, error: null };
    case 'LOAD_CONFIG_ERROR':
      return { ...state, loading: false, error: action.payload };
    case 'RESIZE_SUCCESS':
      return { ...state, gridConfig: action.payload, error: null };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    default:
      return state;
  }
}

function GridProvider({ children }) {
  const [state, dispatch] = useReducer(gridReducer, initialState);

  const loadGridConfig = useCallback(async () => {
    try {
      let config = await db.gridConfig.get('default');
      if (!config) {
        await db.gridConfig.put(DEFAULT_GRID_CONFIG);
        config = DEFAULT_GRID_CONFIG;
      }
      dispatch({ type: 'LOAD_CONFIG_SUCCESS', payload: config });
    } catch (error) {
      dispatch({ type: 'LOAD_CONFIG_ERROR', payload: error.message });
    }
  }, []);

  useEffect(() => {
    loadGridConfig();
  }, [loadGridConfig]);

  const getOrphanedPlants = useCallback(async (newRows, newCols) => {
    const allPlants = await db.plants.toArray();
    return allPlants.filter(
      (plant) => plant.gridRow >= newRows || plant.gridCol >= newCols
    );
  }, []);

  const resizeGrid = useCallback(async (newRows, newCols) => {
    const orphaned = await getOrphanedPlants(newRows, newCols);
    return orphaned;
  }, [getOrphanedPlants]);

  const confirmResize = useCallback(async (newRows, newCols) => {
    try {
      const updatedConfig = { id: 'default', rows: newRows, cols: newCols };
      await db.gridConfig.put(updatedConfig);
      dispatch({ type: 'RESIZE_SUCCESS', payload: updatedConfig });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  }, []);

  const value = {
    gridConfig: state.gridConfig,
    loading: state.loading,
    error: state.error,
    loadGridConfig,
    resizeGrid,
    getOrphanedPlants,
    confirmResize,
  };

  return <GridContext.Provider value={value}>{children}</GridContext.Provider>;
}

export { GridContext, GridProvider };
